/**
 * Shared utilities for detecting and categorizing session errors.
 * Used by both createSession.ts and SessionProvider.tsx
 */

export type SessionType = 'olSession' | 'dbSession' | 'replaySession';

/**
 * Thrown by `Session.request` when a websocket RPC fails for a *session* or
 * *transport* reason — the backend rejected the session (invalid / expired /
 * superseded) or the socket was unavailable — rather than because of the
 * request itself.
 *
 * The reconnect / grace-period flow (`useGracefulLogout`) owns recovery and
 * the user-facing "Checking connection…"/logout UX for these. Shared error
 * consumers (`@ais-ui/custom-hooks`' `classifyApiError`) recognise this type by
 * `name` and suppress a generic error toast — otherwise every polling query
 * (overlay, events, ship info, …) repeats the same "Something went wrong"
 * toast for as long as the session stays invalid.
 *
 * Duck-typed by name on purpose: `custom-hooks` is a lower-level lib and must
 * not depend on `@ais-ui/auth` (mirroring how `ServiceUnavailableError` is
 * recognised).
 */
export class SessionUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SessionUnavailableError';
  }
}

/**
 * Awaits a session request, resolving to `undefined` if the session became
 * unavailable mid-request. That is a dropped transport, which the
 * reconnect/grace-period flow already owns - a caller that fires the request
 * from an effect or event handler has nothing to add, and letting it reject
 * only surfaces as an unhandled rejection. Other failures still throw.
 */
export async function ignoreSessionUnavailable<T>(
  request: PromiseLike<T>,
): Promise<T | undefined> {
  try {
    return await request;
  } catch (error) {
    if (error instanceof SessionUnavailableError) {
      return undefined;
    }
    throw error;
  }
}

// User-facing logout reason strings. Named here so `buildSessionErrorDetail`
// (what produces them) and `categorizeLogoutReason` (what maps them to a
// stable Sentry tag) can't silently drift out of sync.
export const LOGOUT_REASON_NEW_LOGON =
  'New login detected. You have been logged out.';
export const LOGOUT_REASON_SESSION_TIMEOUT = 'Your session has timed out.';
export const LOGOUT_REASON_SESSION_INVALID = 'Your session has expired.';
export const LOGOUT_REASON_RECONNECT_GIVEUP =
  'Could not reconnect to the server after multiple attempts';

const AUTH_FAILURE_PATTERNS = [
  'Invalid session ID',
  'SessionNotFound',
  'invalid session',
] as const;

const NEW_LOGON_PATTERN = 'NewLogonDetected';

const SESSION_TIMEOUT_PATTERN = 'SessionTimeout';

const CONNECTIVITY_PATTERNS = [
  'timeout',
  'Timeout',
  'connection',
  'Connection',
  'network',
  'Network',
] as const;

/**
 * Check if an error message indicates a definite authentication failure.
 * These errors should result in immediate logout.
 */
export function isAuthFailure(errorMessage: string): boolean {
  return AUTH_FAILURE_PATTERNS.some((pattern) =>
    errorMessage.includes(pattern),
  );
}

/**
 * Check if an error message indicates a new login from another session.
 */
export function isNewLogonError(errorMessage: string): boolean {
  return errorMessage.includes(NEW_LOGON_PATTERN);
}

/**
 * Check if an error message indicates a session timeout.
 */
export function isSessionTimeoutError(errorMessage: string): boolean {
  return errorMessage.includes(SESSION_TIMEOUT_PATTERN);
}

/**
 * Check if an error message indicates a connectivity issue.
 * These errors may be transient and worth retrying.
 */
export function isConnectivityIssue(errorMessage: string): boolean {
  return CONNECTIVITY_PATTERNS.some((pattern) =>
    errorMessage.includes(pattern),
  );
}

/**
 * Prefix of what `retireSession` rejects in-flight RPCs with when their socket
 * is retired (replaced by a reconnect, or torn down by logout). Shared with
 * {@link TRANSPORT_UNAVAILABLE_PATTERNS} so the two can't drift apart: a
 * request cut off by a retire is a transport drop, not a request failure.
 */
export const RETIRED_SESSION_REJECTION_PREFIX = 'WebSocket retired';

/**
 * Transport-level "the socket isn't usable" messages produced by
 * `createSession`: the `onclose` reject of in-flight RPCs, the same reject
 * when a reconnect retires the socket, and the pre-send guard that fails fast
 * when the socket isn't OPEN. All are expected connectivity outcomes during an
 * olserver drop/reconnect (already handled by the grace-period/reconnect flow),
 * so `request()` keeps them out of Sentry as errors — capturing every dropped
 * request would flood the project with a burst of non-actionable network-error
 * events on each drop.
 */
const TRANSPORT_UNAVAILABLE_PATTERNS = [
  'WebSocket closed before response',
  RETIRED_SESSION_REJECTION_PREFIX,
  'WebSocket is not open',
] as const;

/**
 * Check if an error message is a transport-level drop signal — the socket
 * closed or was retired mid-request, or a request was attempted on a non-OPEN
 * socket. These are expected during a reconnect and must not be captured as
 * Sentry errors.
 */
export function isTransportUnavailableSignal(errorMessage: string): boolean {
  return TRANSPORT_UNAVAILABLE_PATTERNS.some((pattern) =>
    errorMessage.includes(pattern),
  );
}

/**
 * Backend session-state signals that are surfaced to the client but are
 * *expected* rather than application bugs (the user's session was superseded,
 * expired, or is no longer known to the server). These are already handled by
 * the re-auth/reconnect flow (a `sessionError` event is dispatched), so they
 * should NOT be reported to Sentry as errors — doing so floods the project with
 * non-actionable noise (issues CB, FMM).
 *
 * Note: this deliberately does NOT include "user not found", which is a genuine
 * backend internal error (issue FMK) and must remain visible in Sentry.
 */

/**
 * Check if an error message is an expected backend session/auth signal that is
 * handled by the re-auth/reconnect flow and must not be captured as a Sentry
 * error. Covers new-logon, session-timeout and generic auth failures (invalid /
 * not-found session).
 */
export function isExpectedSessionSignal(errorMessage: string): boolean {
  return (
    isAuthFailure(errorMessage) ||
    isNewLogonError(errorMessage) ||
    isSessionTimeoutError(errorMessage)
  );
}

export interface SessionErrorDetail {
  errorMessage: string;
  isNewLogon: boolean;
  isSessionTimeout: boolean;
  /** User-facing logout reason, or null if not an auth error requiring logout. */
  logoutReason: string | null;
  /** Which websocket session (olSession/dbSession/replaySession) reported this signal. */
  sessionType: SessionType;
}

/**
 * Build a single session error detail with classification and user-facing message.
 * This is the single source of truth for mapping server errors to logout reasons.
 * SessionProvider should use `logoutReason` directly — no re-classification needed.
 */
export function buildSessionErrorDetail(
  errorMessage: string,
  sessionType: SessionType,
): SessionErrorDetail {
  if (isNewLogonError(errorMessage)) {
    return {
      errorMessage,
      isNewLogon: true,
      isSessionTimeout: false,
      logoutReason: LOGOUT_REASON_NEW_LOGON,
      sessionType,
    };
  }
  if (isSessionTimeoutError(errorMessage)) {
    return {
      errorMessage,
      isNewLogon: false,
      isSessionTimeout: true,
      logoutReason: LOGOUT_REASON_SESSION_TIMEOUT,
      sessionType,
    };
  }
  if (isAuthFailure(errorMessage)) {
    return {
      errorMessage,
      isNewLogon: false,
      isSessionTimeout: false,
      logoutReason: LOGOUT_REASON_SESSION_INVALID,
      sessionType,
    };
  }
  return {
    errorMessage,
    isNewLogon: false,
    isSessionTimeout: false,
    logoutReason: null,
    sessionType,
  };
}

/**
 * Categorize an error message for Sentry tagging.
 */
export function categorizeErrorMessage(errorMessage: string): string {
  if (
    errorMessage.includes('Invalid session ID') ||
    errorMessage.includes('invalid session')
  ) {
    return 'invalid_session';
  }
  if (errorMessage.includes(NEW_LOGON_PATTERN)) {
    return 'new_logon_detected';
  }
  if (errorMessage.includes(SESSION_TIMEOUT_PATTERN)) {
    return 'session_timeout';
  }
  if (errorMessage.includes('SessionNotFound')) {
    return 'session_not_found';
  }
  if (
    CONNECTIVITY_PATTERNS.some((p) =>
      errorMessage.toLowerCase().includes(p.toLowerCase()),
    )
  ) {
    return 'connectivity';
  }
  return 'unknown';
}

/** Stable Sentry tag values for `logout()`'s `logout_reason_category` tag. */
export type LogoutReasonCategory =
  | 'new_logon'
  | 'session_timeout'
  | 'session_invalid'
  | 'reconnect_giveup'
  | 'manual'
  | 'unknown';

/**
 * Categorize a `logout(reason)` reason string into a stable Sentry tag value.
 * An empty reason is a manual, user-initiated logout (see `UserDropdown`'s
 * `logout('')`); every other known reason is one of the forced-logout
 * messages produced by `buildSessionErrorDetail` or the reconnect give-up
 * path. Kept in sync with those reason strings by referencing the same
 * exported constants rather than duplicating the literal text.
 */
export function categorizeLogoutReason(reason: string): LogoutReasonCategory {
  if (!reason) {
    return 'manual';
  }
  switch (reason) {
    case LOGOUT_REASON_NEW_LOGON:
      return 'new_logon';
    case LOGOUT_REASON_SESSION_TIMEOUT:
      return 'session_timeout';
    case LOGOUT_REASON_SESSION_INVALID:
      return 'session_invalid';
    case LOGOUT_REASON_RECONNECT_GIVEUP:
      return 'reconnect_giveup';
    default:
      return 'unknown';
  }
}
